import { RefObject, useRef, useState } from 'react';
import styles from './selectPlayerModal.module.css';
import {
  DropdownMenuAction,
  ImageType,
  NewPlayerProps,
} from './selectPlayerModal.types';
import { Trans, useTranslation } from 'react-i18next';
import { convertFileToBase64 } from '../../utils/helper';
import edit from '../../assets/edit.png';
import gallery from '../../assets/gallery.png';
import DropdownMenu from '../dropDownMenu/dropDownMenu';

const NewPlayer = ({ images, setImages }: NewPlayerProps) => {
  const portraitInputRef: RefObject<HTMLInputElement> =
    useRef<HTMLInputElement>(null);
  const fullBodyInputRef: RefObject<HTMLInputElement> =
    useRef<HTMLInputElement>(null);
  const [showDropdownMenu, setShowDropdownMenu] = useState<
    ImageType | undefined
  >(undefined);
  const { t } = useTranslation();

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: ImageType,
  ) => {
    const file: File | undefined = event.target.files?.[0];
    if (file) {
      try {
        const base64: string = await convertFileToBase64(file);
        setImages((prev) => ({
          ...prev,
          [type]: base64,
        }));
      } catch (error) {
        console.error('Error converting file to base64:', error);
      }
    }
    event.target.value = '';
  };

  const handleMenuSelect = (action: DropdownMenuAction, type: ImageType) => {
    if (action === DropdownMenuAction.Replace) {
      if (type === ImageType.Portrait) {
        portraitInputRef.current?.click();
      } else {
        fullBodyInputRef.current?.click();
      }
    } else if (action === DropdownMenuAction.Delete) {
      setImages((prev) => ({
        ...prev,
        [type]: '',
      }));
    }
    setShowDropdownMenu(undefined);
  };

  const handlePlaceholderClick = (type: ImageType) => {
    const hasImage: boolean =
      type === ImageType.Portrait
        ? !!images.portraitImage
        : !!images.fullBodyImage;

    if (!hasImage) {
      if (type === ImageType.Portrait) {
        portraitInputRef.current?.click();
      } else {
        fullBodyInputRef.current?.click();
      }
    }
  };

  return (
    <>
      <h3>
        <Trans i18nKey={'selectPlayer.playersPhotos'} />
      </h3>
      <div className={styles.imageGroup}>
        <input
          type="file"
          accept="image/*"
          ref={portraitInputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleImageUpload(e, ImageType.Portrait)}
        />
        <input
          type="file"
          accept="image/*"
          ref={fullBodyInputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleImageUpload(e, ImageType.FullBody)}
        />

        <div
          className={styles.portraitPlaceholder}
          onClick={() => handlePlaceholderClick(ImageType.Portrait)}
        >
          {images.portraitImage ? (
            <>
              <div
                className={styles.editIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdownMenu(ImageType.Portrait);
                }}
              >
                <img src={edit} alt="edit" />
                {showDropdownMenu === ImageType.Portrait && (
                  <DropdownMenu
                    className={styles.dropdownMenu}
                    options={[
                      {
                        label: 'selectPlayer.replacePhoto',
                        optionKey: DropdownMenuAction.Replace,
                      },
                      {
                        label: 'selectPlayer.deletePhoto',
                        optionKey: DropdownMenuAction.Delete,
                      },
                    ]}
                    onSelect={(action: DropdownMenuAction) =>
                      handleMenuSelect(action, ImageType.Portrait)
                    }
                    onClose={() => setShowDropdownMenu(undefined)}
                  />
                )}
              </div>

              <div className={styles.imageWrapper}>
                <img
                  src={images.portraitImage}
                  alt={t('selectPlayer.portraitPreview')}
                  className={styles.previewImage}
                />
              </div>
            </>
          ) : (
            <>
              <img src={gallery} alt="" />
              <span>
                <Trans i18nKey={'selectPlayer.uploadPortrait'} />
              </span>
            </>
          )}
        </div>

        <div
          className={styles.fullBodyPlaceholder}
          onClick={() => handlePlaceholderClick(ImageType.FullBody)}
        >
          {images.fullBodyImage ? (
            <>
              <div
                className={styles.editIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdownMenu(ImageType.FullBody);
                }}
              >
                <img src={edit} alt="" />
                {showDropdownMenu === ImageType.FullBody && (
                  <DropdownMenu
                    className={styles.dropdownMenu}
                    options={[
                      {
                        label: 'selectPlayer.replacePhoto',
                        optionKey: DropdownMenuAction.Replace,
                      },
                      {
                        label: 'selectPlayer.deletePhoto',
                        optionKey: DropdownMenuAction.Delete,
                      },
                    ]}
                    onSelect={(action: DropdownMenuAction) =>
                      handleMenuSelect(action, ImageType.FullBody)
                    }
                    onClose={() => setShowDropdownMenu(undefined)}
                  />
                )}
              </div>

              <div className={styles.imageWrapper}>
                <img
                  src={images.fullBodyImage}
                  alt={t('selectPlayer.fullBodyPreview')}
                  className={styles.previewImage}
                />
              </div>
            </>
          ) : (
            <>
              <img src={gallery} alt="" />
              <span>
                <Trans i18nKey={'selectPlayer.uploadFullBody'} />
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NewPlayer;
